import React, {
  FC,
  SelectHTMLAttributes,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CaretDown } from '..';
import ErrorMessage from '../error-message/error-message';

type OptionType = {
  name: string;
  value: string;
};

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  selectLabel: string;
  options: OptionType[];
  width?: 'full' | 'lg' | 'md';
  positioning?: 'relative' | 'absolute';
  selectedOption?: OptionType;
  setSelectedOption?: Dispatch<SetStateAction<OptionType>>;
  onChange?: any;
  errors?: any;
  name?: string;
  withLabel?: boolean;
  isValid?: boolean;
  errorMessage?: string;
  largeHeight?: boolean;
  cleared?: boolean;
  setCleared?: Dispatch<SetStateAction<boolean>>;
  pillVariant?: boolean;
  disabled?: boolean;
}

export const RegularSelect: FC<SelectProps> = ({
  className,
  cleared = false,
  selectLabel,
  options = [],
  positioning,
  selectedOption = { name: '', value: '' },
  setSelectedOption,
  setCleared,
  width,
  onChange,
  errors,
  name,
  withLabel,
  isValid = true,
  errorMessage,
  largeHeight,
  pillVariant,
  disabled,
}: SelectProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<OptionType>(selectedOption);
  const [defaultVal] = useState<string | undefined>(selectLabel);
  const [label] = useState<string>(selectLabel);
  const [visible, setVisible] = useState<boolean>(false);
  const inactivePillClasses = `w-[100%] flex justify-center items-center h-[34px] mt-2 rounded-[4px] cursor-pointer bg-[#F8F9FB] border border-solid border-[#CCCCCC] hover:brightness-95`;
  const activePillClasses = `w-[100%] flex justify-center items-center h-[34px] mt-2 rounded-[4px] cursor-pointer bg-[#FFF] border-2 border-solid border-[#FF5000] text-[#FF5000] hover:bg-[#FF5000] hover:text-white`;

  const handleOnClick = (
    item: OptionType,
    e: React.SyntheticEvent<EventTarget>
  ): void => {
    e.preventDefault();
    if (selected.name !== item.name) {
      setSelected({ ...item });
      setSelectedOption && setSelectedOption(item);
    }
  };

  // for any selectedOption changes outside of interaction with the component.
  useEffect(() => {
    setSelected(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (cleared) {
      setSelected({ name: '', value: '' });
      setCleared && setCleared(false);
    }

    const handleMouseDownClose = (e: MouseEvent) => {
      if (
        visible &&
        ref.current &&
        !ref.current.contains(e.target as HTMLElement)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDownClose);
    return () => {
      document.removeEventListener('mousedown', handleMouseDownClose);
    };
  }, [visible, cleared, setSelected, setCleared]);

  return (
    <div
      ref={ref}
      className={`relative ${
        width === 'full'
          ? 'w-full'
          : width === 'lg'
          ? 'w-1/2'
          : width === 'md'
          ? 'w-52'
          : 'w-44'
      } ${className ? className : ''}`}
    >
      <Listbox
        value={selectedOption}
        as="div"
        className={`text-left cursor-pointer`}
        onChange={(e: any) => {
          onChange && onChange(e);
          setSelected(e);
        }}
        onClick={() => !disabled && setVisible((visible) => !visible)}
        disabled={disabled}
      >
        <>
          <Listbox.Button
            as="div"
            className={`w-full peer relative  ${
              visible
                ? 'border-b-2 border-thryv-black-500'
                : 'border-b text-thryv-steel border-thryv-gray-light-500'
            } transition-all`}
            tabIndex={0}
            data-testid="select"
            role="button"
          >
            <div>
              <span
                className={`absolute ${
                  visible || withLabel
                    ? '-top-5 opacity-100'
                    : 'top-0 opacity-0'
                } left-0 ${
                  errors &&
                  name &&
                  errors[name] &&
                  (errors[name].value?.message || errors[name]?.message)
                    ? 'text-utility-error-danger'
                    : 'text-thryv-steel'
                } text-label-text mb-2 transition-all duration-400`}
              >
                {label}
              </span>
              <div className="mt-2 flex justify-between items-center">
                <div className="w-5/6 truncate">
                  {!visible ? (
                    defaultVal!.length > 0 ? (
                      <>
                        {selected.name.length === 0 ? (
                          <span
                            className="text-thryv-steel text-form-text mb-5 transition-all"
                            data-testid="select-option"
                          >
                            {defaultVal}
                          </span>
                        ) : (
                          <span
                            className="text-thryv-black-500 text-form-text mb-2 transition-all"
                            data-testid="select-option"
                          >
                            {selected.name}
                          </span>
                        )}
                      </>
                    ) : (
                      <span
                        className="text-thryv-black-500 text-form-text mb-2 transition-all"
                        data-testid="select-option"
                      >
                        {selected.name}
                      </span>
                    )
                  ) : (
                    <span
                      className="text-thryv-black-500 text-form-text mb-2 transition-all"
                      data-testid="select-option"
                    >
                      -- Select --
                    </span>
                  )}
                </div>
                <CaretDown
                  className={`fill-current ml-6 ${
                    visible ? 'text-thryv-black-500' : ''
                  }`}
                />
              </div>
            </div>
          </Listbox.Button>

          <Transition
            as="div"
            enter="transition ease-in duration-300 relative z-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-out duration-200"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Listbox.Options
              className={`overflow-auto ${largeHeight ? 'h-64' : 'h-32'} 
               w-full mt-3 origin-top-left bg-white rounded shadow-thryv focus:outline-none ${
                 positioning || 'absolute'
               } z-100 ${pillVariant ? 'px-3 pb-2' : ''}`}
              static
            >
              <div>
                {options.map((option, i) => (
                  <div key={i}>
                    <Listbox.Option value={option}>
                      {({ active }) => (
                        <button
                          className={` flex justify-start items-center ${
                            active &&
                            'bg-thryv-white-300 transition-all duration-300'
                          } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all ${
                            pillVariant &&
                            selectedOption?.value !== option?.value
                              ? inactivePillClasses
                              : ''
                          } ${
                            pillVariant &&
                            selectedOption?.value === option?.value
                              ? activePillClasses
                              : ''
                          }`}
                          onClick={(e) => handleOnClick(option, e)}
                          disabled={disabled}
                        >
                          <span className="ml-1">{option.name}</span>
                        </button>
                      )}
                    </Listbox.Option>
                  </div>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </>
      </Listbox>
      {errors && name && errors[name]?.value?.message && (
        <ErrorMessage errorMessage={errors[name].value.message} />
      )}
      {errors &&
        name &&
        !errors[name]?.value?.message &&
        errors[name]?.message && (
          <ErrorMessage errorMessage={errors[name].message} />
        )}
      {!isValid && errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
};
